import { useState, useEffect, useCallback } from 'react';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';

import { useMessagingClient } from './useMessagingClient';
import { ErrorCode, RequestState, RequestStateType } from '../RequestState';
import { Message, Conversation } from '../types';

export type { RequestState } from '../RequestState';
export { RequestStateType } from '../RequestState';

export enum ConversationState {
    LOADING = 'loading',
    LOADING_MORE_MESSAGES = 'loading more messages',
    NO_MESSAGES = 'no messages',
    MID_CONVERSATION = 'mid conversation',
    END_OF_CONVERSATION = 'end of conversation',
    NOT_FOUND = 'not found',
    ERROR = 'error',
}

function getDerivedConversationState(
    messages: readonly Message[],
    messagesRequestState: RequestState,
    oldestMessageUuid?: string,
): ConversationState {
    if (messagesRequestState.state === RequestStateType.ERROR) {
        if (
            messagesRequestState.error instanceof Error &&
            messagesRequestState.error.message === ErrorCode.NOT_FOUND
        ) {
            return ConversationState.NOT_FOUND;
        }
    }

    if (messages.length === 0) {
        if (messagesRequestState.state === RequestStateType.LOADING) {
            return ConversationState.LOADING;
        }

        return ConversationState.NO_MESSAGES;
    }

    if (messagesRequestState.state === RequestStateType.LOADING) {
        return ConversationState.LOADING_MORE_MESSAGES;
    }

    const lastMessage: Message | undefined = messages[messages.length - 1];
    if (!!lastMessage && lastMessage.uuid === oldestMessageUuid) {
        return ConversationState.END_OF_CONVERSATION;
    }

    return ConversationState.MID_CONVERSATION;
}

export function useConversation(uuid: string): {
    conversation: Conversation | undefined;
    messages: readonly Message[];
    messagesRequestState: RequestState;
    requestMoreMessages: () => void;
    sendMessage: (content: string) => void;
    sendMessageRequestState: RequestState;
    sendMessageReadReceipt: (message: Message) => void;
    conversationState: ConversationState;
} {
    const messagingClient = useMessagingClient();

    // LOCAL STATE

    const [messagesRequestState, setMessagesUi] = useState<RequestState>({
        state: RequestStateType.IDLE,
    });
    const [sendMessageRequestState, setSendMessageUi] = useState<RequestState>({
        state: RequestStateType.IDLE,
    });

    // EXTERNAL STATE

    const conversation: ReturnType<typeof messagingClient.selectors.getConversation> =
        useSyncExternalStoreWithSelector(
            messagingClient.subscribe,
            messagingClient.getState,
            undefined,
            state => messagingClient.selectors.getConversation(state, uuid),
        );

    const messages: ReturnType<typeof messagingClient.selectors.getMessages> =
        useSyncExternalStoreWithSelector(
            messagingClient.subscribe,
            messagingClient.getState,
            undefined,
            state => messagingClient.selectors.getMessages(state, uuid),
        );

    const oldestMessageUuid: ReturnType<typeof messagingClient.selectors.getOldestMessageUuid> =
        useSyncExternalStoreWithSelector(
            messagingClient.subscribe,
            messagingClient.getState,
            undefined,
            state => messagingClient.selectors.getOldestMessageUuid(state, uuid),
        );

    // API

    const sendMessage = useCallback(
        (content: string) => {
            if (sendMessageRequestState.state === RequestStateType.LOADING) {
                return;
            }
            setSendMessageUi({ state: RequestStateType.LOADING });
            messagingClient
                .sendMessage(uuid, content)
                .then(() => setSendMessageUi({ state: RequestStateType.IDLE }))
                .catch(error => setSendMessageUi({ state: RequestStateType.ERROR, error }));
        },
        [uuid, messagingClient, sendMessageRequestState, setSendMessageUi],
    );

    const sendMessageReadReceipt = useCallback(
        (message: Message) => {
            messagingClient.sendMessageReadReceipt(message).catch(() => {});
        },
        [messagingClient],
    );

    const lastMessage: Message | undefined = messages[messages.length - 1];
    const requestMoreMessages = useCallback(() => {
        if (messagesRequestState.state === RequestStateType.LOADING) {
            return;
        }
        setMessagesUi({ state: RequestStateType.LOADING });
        messagingClient
            .getMessages(uuid, { after: lastMessage?.uuid })
            .then(() => setMessagesUi({ state: RequestStateType.IDLE }))
            .catch(error => setMessagesUi({ state: RequestStateType.ERROR, error }));
    }, [uuid, messagingClient, lastMessage, setMessagesUi, messagesRequestState]);

    // LIFECYCLE

    useEffect(() => {
        // Do not request messages on mount if we already have 10 in
        // state or if the last message we have loaded is the oldest
        // message in the conversation.
        if (messages.length >= 10 || (lastMessage && lastMessage.uuid === oldestMessageUuid)) {
            return;
        }
        setMessagesUi({ state: RequestStateType.LOADING });
        messagingClient
            .getMessages(uuid)
            .then(() => setMessagesUi({ state: RequestStateType.IDLE }))
            .catch(error => setMessagesUi({ state: RequestStateType.ERROR, error }));
    }, [uuid]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        conversation,
        messages,
        messagesRequestState,
        requestMoreMessages,
        sendMessage,
        sendMessageRequestState,
        sendMessageReadReceipt,
        conversationState: getDerivedConversationState(
            messages,
            messagesRequestState,
            oldestMessageUuid,
        ),
    };
}
